import { shortDate } from '../../lib/formatLabels';
import { scale, smoothPath, segments } from './svgPath';

export interface MultiLineSeries {
  name: string;
  data: Array<{ x: string; y: number }>;
}

interface Props {
  series: MultiLineSeries[];
  height?: number;
}

const STYLES = [
  { stroke: 'stroke-slate', swatch: 'bg-slate', dot: 'fill-slate' },
  { stroke: 'stroke-ink', swatch: 'bg-ink', dot: 'fill-ink' },
  { stroke: 'stroke-accent', swatch: 'bg-accent', dot: 'fill-accent' },
  { stroke: 'stroke-muted', swatch: 'bg-muted', dot: 'fill-muted' },
];

export default function MultiLineTrendChart({ series, height = 280 }: Props) {
  if (series.length === 0) {
    return <p className="text-bodydark2 text-sm">No data in window.</p>;
  }
  const xs: string[] = [];
  series.forEach((s) => s.data.forEach((p) => { if (!xs.includes(p.x)) xs.push(p.x); }));
  xs.sort();
  const x = scale(0, Math.max(1, xs.length - 1), 50, 740);
  const y = scale(0, 100, 200, 20);
  const ticks = [0, 20, 40, 60, 80, 100];
  const every = Math.max(1, Math.ceil(xs.length / 6));
  const lines = series.map((s, n) => {
    const values: (number | null)[] = xs.map(() => null);
    s.data.forEach((p) => { values[xs.indexOf(p.x)] = p.y; });
    return { name: s.name, style: STYLES[n % STYLES.length], runs: segments(values) };
  });
  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted">
        {lines.map((l) => (
          <span key={l.name} className="inline-flex items-center gap-1.5">
            <span className={`h-2.5 w-2.5 rounded-[3px] ${l.style.swatch}`} />{l.name}
          </span>
        ))}
      </div>
      <svg viewBox="0 0 760 240" className="block h-auto w-full" style={{ maxHeight: height }} role="img" aria-label="Score trend by series">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={40} x2={750} y1={y(t)} y2={y(t)} className="stroke-line" />
            <text x={30} y={y(t) + 4} textAnchor="end" className="fill-muted font-mono text-[11px]">{t}</text>
          </g>
        ))}
        {lines.map((l) => l.runs.map((run, k) => run.length === 1 ? (
          <circle key={`${l.name}-${k}`} cx={x(run[0].i)} cy={y(run[0].v)} r={3.5} className={l.style.dot} />
        ) : (
          <path key={`${l.name}-${k}`} d={smoothPath(run.map((p) => [x(p.i), y(p.v)] as [number, number]))}
            fill="none" className={l.style.stroke} strokeWidth={3} strokeLinejoin="round" strokeLinecap="round" />
        )))}
        {xs.map((v, i) => (i % every === 0 || i === xs.length - 1) && (
          <text key={v} x={x(i)} y={230} textAnchor="middle" className="fill-muted font-mono text-[11px]">{shortDate(v)}</text>
        ))}
      </svg>
    </div>
  );
}
